import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readAttachmentContent } from '@evogard/ui';
import type {
  ClaimActivity,
  ClaimDeadlineFilterValue,
  DepartmentLabels,
  ClaimComment,
  ClaimFile,
  Peripheral,
  PeripheralForm,
  PeripheralItemWrite,
  PeripheralMoveResult,
  PeripheralQuoteWrite,
  PeripheralSummary,
} from '@evogard/contract';
import { loadFailureMessage, type PeripheralStage } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import type { ContactAttemptInput } from '../claim/DeskRecords.js';
import { can, useSessionContext } from '../../../lib/session.js';

/**
 * A conversa de Periféricos com a API (`/claims/peripherals`, 08/10/2026).
 *
 * **Toda chave começa com `['sinistros', 'perifericos']`**, e toda escrita
 * invalida a raiz inteira: mover muda a fase na lista, no quadro, na ficha e
 * na trilha de uma vez — a mesma regra de `claim/useDesk.ts`.
 */

const ROOT = ['sinistros', 'perifericos'] as const;

/** A recusa da API, com o código dela — a tela decide pelo código, e não pela frase. */
export class PeripheralRefusal extends Error {
  constructor(
    message: string,
    readonly code: string | null,
  ) {
    super(message);
  }
}

function refusal(r: { status: number; body: unknown }, fallback: string): never {
  const body = r.body as { message?: string; code?: string } | undefined;
  throw new PeripheralRefusal(body?.message ?? fallback, body?.code ?? null);
}

function useTenantId() {
  const { data: context } = useSessionContext();
  return context?.currentTenant.id;
}

export function usePeripheralPermissions() {
  const { data: context } = useSessionContext();
  return {
    canCreate: can(context, 'claims.peripherals', 'criar'),
    canMove: can(context, 'claims.peripherals', 'mover'),
    canSupervise: can(context, 'claims.peripherals', 'supervisionar'),
    canArchive: can(context, 'claims.peripherals', 'editar'),
    canComment: can(context, 'claims.peripherals', 'comentar'),
  };
}

export interface PeripheralFilter {
  search?: string;
  stage?: PeripheralStage;
  includeArchived?: boolean;
  labelId?: number;
  deadline?: ClaimDeadlineFilterValue;
  /** `true`: só os parados; `false`: só os que não estão; ausente, os dois. */
  onHold?: boolean;
}

export async function fetchPeripheralPage(
  filter: PeripheralFilter,
  page: number,
  perPage: number,
) {
  const r = await api.claimPeripherals.listPeripherals({
    query: {
      page,
      perPage,
      search: filter.search || undefined,
      stage: filter.stage,
      includeArchived: filter.includeArchived ? 'true' : undefined,
      labelId: filter.labelId,
      deadline: filter.deadline,
      onHold: filter.onHold === undefined ? undefined : filter.onHold ? 'true' : 'false',
    },
  });
  if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os acionamentos'));
  return r.body;
}

export function usePeripheralList(
  filter: PeripheralFilter,
  page: number,
  perPage: number,
) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'lista', tenantId, filter, page, perPage],
    queryFn: () => fetchPeripheralPage(filter, page, perPage),
    enabled: tenantId !== undefined,
    placeholderData: (previous) => previous,
  });
}

export function usePeripheral(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'ficha', tenantId, id],
    queryFn: async (): Promise<Peripheral> => {
      const r = await api.claimPeripherals.getPeripheral({ params: { id: id! } });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'o acionamento'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/**
 * As etapas por onde o acionamento já passou — as das passagens FECHADAS. É o
 * que a trilha pergunta para saber qual etapa anterior se corrige.
 */
export function usePeripheralVisited(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'passagens', tenantId, id],
    queryFn: async (): Promise<string[]> => {
      const r = await api.claimPeripherals.listPeripheralPassages({
        params: { id: id! },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as passagens'));
      return [...new Set(r.body.filter((x) => x.leftAt !== null).map((x) => x.phase))];
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

export function usePeripheralForm(id: number | null, phase: string | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'formulario', tenantId, id, phase],
    queryFn: async (): Promise<PeripheralForm> => {
      const r = await api.claimPeripherals.getPeripheralForm({
        params: { id: id!, phase: phase! },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'o formulário'));
      return r.body;
    },
    enabled: id !== null && phase !== null && tenantId !== undefined,
  });
}

export async function peripheralFileUrl(id: number, documentId: number): Promise<string> {
  const r = await api.claimPeripherals.peripheralDocumentUrl({
    params: { id, documentId },
  });
  return ok(r, 200, 'Não foi possível abrir o arquivo.').url;
}

/** O comprovante da PI paga — a URL assinada do PDF que a casa guardou. */
export async function peripheralReceiptUrl(id: number): Promise<string> {
  const r = await api.claimPeripherals.paymentReceiptUrl({ params: { id } });
  return ok(r, 200, 'Não foi possível abrir o comprovante.').url;
}

/** Quem pode ser responsável — o seletor do topo da mesa. */
export function usePeripheralAssigneeOptions(enabled: boolean) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'responsaveis', tenantId],
    queryFn: async () => {
      const r = await api.claimPeripherals.listPeripheralAssigneeOptions();
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'quem pode ser responsável'));
      return r.body;
    },
    enabled: enabled && tenantId !== undefined,
    staleTime: 5 * 60_000,
  });
}

/**
 * A cobertura de Periféricos de cada veículo da busca, lida do contrato. A
 * resposta vira um mapa por código; enquanto ela não chega, o código não
 * está no mapa — e a tela não deixa escolher o que ainda não sabe.
 */
export function useVehicleCoverage(vehicleCodes: readonly string[]) {
  const tenantId = useTenantId();
  const codes = [...new Set(vehicleCodes)].sort();
  return useQuery({
    queryKey: [...ROOT, 'cobertura', tenantId, codes],
    queryFn: async () => {
      const r = await api.claimPeripherals.listPeripheralVehicleCoverage({
        body: { vehicleCodes: codes },
      });
      if (r.status !== 200)
        throw new Error(loadFailureMessage(r.status, 'a cobertura dos veículos'));
      return new Map(r.body.map((v) => [v.vehicleCode, v.coverage]));
    },
    enabled: tenantId !== undefined && codes.length > 0,
  });
}

/** Abrir um acionamento. */
export function useCreatePeripheral() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (body: { vehicleCode: string; occurrenceDescription: string }) => {
      const r = await api.claimPeripherals.createPeripheral({ body });
      if (r.status !== 201) refusal(r, 'Não foi possível abrir o acionamento.');
      return r.body as Peripheral;
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ROOT }),
  });
}

/**
 * As escritas da mesa de um acionamento. Cada uma devolve o que a API
 * devolveu e invalida a raiz: a lista, o quadro, a ficha e o formulário se
 * releem juntos.
 */
export function usePeripheralMutations(id: number) {
  const client = useQueryClient();
  const done = () => client.invalidateQueries({ queryKey: ROOT });
  const useWrite = <A, R>(fn: (args: A) => Promise<R>) =>
    useMutation({ mutationFn: fn, onSuccess: done });

  return {
    save: useWrite(
      async (a: { phase: string; values: Record<string, string | null> }) => {
        const r = await api.claimPeripherals.savePeripheralForm({
          params: { id, phase: a.phase },
          body: { values: a.values },
        });
        if (r.status !== 200) refusal(r, 'Não foi possível salvar.');
        return r.body as PeripheralForm;
      },
    ),
    move: useWrite(async (body: { fromPhase: string; toPhase: string }) => {
      const r = await api.claimPeripherals.movePeripheral({ params: { id }, body });
      if (r.status !== 200) refusal(r, 'Não foi possível mover o acionamento.');
      return r.body as PeripheralMoveResult;
    }),
    hold: useWrite(
      async (body: {
        fromPhase: string;
        type: 'pendencia' | 'pre_desistencia' | 'juridico';
        text: string;
      }) => {
        const r = await api.claimPeripherals.holdPeripheral({ params: { id }, body });
        if (r.status !== 200) refusal(r, 'Não foi possível parar o acionamento.');
        return r.body as PeripheralMoveResult;
      },
    ),
    /* A tentativa de contato (19/10/2026), a mesma da Análise: o print sobe em base64. */
    registerContact: useWrite(async ({ print, ...body }: ContactAttemptInput) => {
      const r = await api.claimPeripherals.registerPeripheralContact({
        params: { id },
        body: {
          ...body,
          attachment: {
            fileName: print.file.name,
            mimeType: print.typeMime,
            content: await readAttachmentContent(print.file),
          },
        },
      });
      if (r.status !== 201) refusal(r, 'Não foi possível registrar o contato.');
      return r.body as PeripheralMoveResult;
    }),
    resume: useWrite(async (body: { fromPhase: string; note?: string }) => {
      const r = await api.claimPeripherals.resumePeripheral({ params: { id }, body });
      if (r.status !== 200) refusal(r, 'Não foi possível retomar o acionamento.');
      return r.body as PeripheralMoveResult;
    }),
    attach: useWrite(async (a: { phase: string; field: string; file: File }) => {
      const r = await api.claimPeripherals.attachPeripheralFile({
        params: { id },
        body: {
          phase: a.phase,
          field: a.field,
          fileName: a.file.name,
          mimeType: a.file.type || 'application/octet-stream',
          content: await readAttachmentContent(a.file),
        },
      });
      if (r.status !== 201) refusal(r, 'Não foi possível enviar o arquivo.');
      return r.body as PeripheralForm;
    }),
    archiveDocument: useWrite(async (documentId: number) => {
      const r = await api.claimPeripherals.archivePeripheralDocument({
        params: { id, documentId },
        body: {},
      });
      if (r.status !== 200) refusal(r, 'Não foi possível tirar o arquivo.');
    }),
    setAssignee: useWrite(async (userId: string | null) => {
      const r = await api.claimPeripherals.setPeripheralAssignee({
        params: { id },
        body: { userId },
      });
      if (r.status !== 200) refusal(r, 'Não foi possível definir o responsável.');
      return r.body as Peripheral;
    }),
    /* O link novo da PI (19/10/2026): arquiva o anterior e reenvia a mensagem. */
    regeneratePaymentLink: useWrite(async () => {
      const r = await api.claimPeripherals.regeneratePaymentLink({
        params: { id },
        body: {},
      });
      if (r.status !== 200) refusal(r, 'Não foi possível gerar o link novo.');
      return r.body as Peripheral;
    }),
    archive: useWrite(async () => {
      const r = await api.claimPeripherals.archivePeripheral({
        params: { id },
        body: {},
      });
      if (r.status !== 200) refusal(r, 'Não foi possível arquivar o acionamento.');
      return r.body as Peripheral;
    }),
    createItem: useWrite(async (body: PeripheralItemWrite) => {
      const r = await api.claimPeripherals.createPeripheralItem({ params: { id }, body });
      if (r.status !== 201) refusal(r, 'Não foi possível acionar o item.');
      return r.body as Peripheral;
    }),
    updateItem: useWrite(
      async (a: { itemId: number; body: Partial<PeripheralItemWrite> }) => {
        const r = await api.claimPeripherals.updatePeripheralItem({
          params: { id, itemId: a.itemId },
          body: a.body,
        });
        if (r.status !== 200) refusal(r, 'Não foi possível corrigir o item.');
        return r.body as Peripheral;
      },
    ),
    archiveItem: useWrite(async (itemId: number) => {
      const r = await api.claimPeripherals.archivePeripheralItem({
        params: { id, itemId },
        body: {},
      });
      if (r.status !== 200) refusal(r, 'Não foi possível tirar o item.');
      return r.body as Peripheral;
    }),
    attachItemPhoto: useWrite(async (a: { itemId: number; kind: string; file: File }) => {
      const r = await api.claimPeripherals.attachPeripheralItemPhoto({
        params: { id, itemId: a.itemId },
        body: {
          kind: a.kind,
          fileName: a.file.name,
          mimeType: a.file.type || 'application/octet-stream',
          content: await readAttachmentContent(a.file),
        },
      });
      if (r.status !== 201) refusal(r, 'Não foi possível enviar a foto.');
      return r.body as Peripheral;
    }),
    createQuote: useWrite(async (a: { itemId: number; body: PeripheralQuoteWrite }) => {
      const r = await api.claimPeripherals.createPeripheralQuote({
        params: { id, itemId: a.itemId },
        body: a.body,
      });
      if (r.status !== 201) refusal(r, 'Não foi possível cadastrar a cotação.');
      return r.body as Peripheral;
    }),
    updateQuote: useWrite(async (a: { quoteId: number; body: PeripheralQuoteWrite }) => {
      const r = await api.claimPeripherals.updatePeripheralQuote({
        params: { id, quoteId: a.quoteId },
        body: a.body,
      });
      if (r.status !== 200) refusal(r, 'Não foi possível corrigir a cotação.');
      return r.body as Peripheral;
    }),
    decideQuote: useWrite(
      async (a: { quoteId: number; decision: 'aprovado' | 'reprovado' }) => {
        const r = await api.claimPeripherals.decidePeripheralQuote({
          params: { id, quoteId: a.quoteId },
          body: { decision: a.decision },
        });
        if (r.status !== 200) refusal(r, 'Não foi possível decidir a cotação.');
        return r.body as Peripheral;
      },
    ),
  };
}

/* ------------------------------------------------------------------------- *
 * A mesa: anexos, atividade, etiquetas e vencimento (15/10/2026) — os mesmos
 * do sinistro, pelas rotas do acionamento.
 * ------------------------------------------------------------------------- */

/** As etiquetas dos setores de quem usa, pela rota de Periféricos (`claims.peripherals:ver`). */
export function usePeripheralLabelOptions() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'etiquetas', tenantId],
    queryFn: async (): Promise<DepartmentLabels[]> => {
      const r = await api.claimPeripherals.listPeripheralLabelOptions();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'as etiquetas'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** Quantos acionamentos de cada etapa estão com o SLA expirado. `version` sobe a cada abertura da mesa. */
export function usePeripheralSlaSummary(version: number) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'sla-resumo', tenantId, version],
    queryFn: async () => {
      const r = await api.claimPeripherals.peripheralSlaSummary();
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os SLAs'));
      return r.body;
    },
    enabled: tenantId !== undefined,
  });
}

/** Todos os arquivos do acionamento: os dos campos das fases e as fotos dos itens. */
export function usePeripheralFiles(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'arquivos', tenantId, id],
    queryFn: async (): Promise<ClaimFile[]> => {
      const r = await api.claimPeripherals.listPeripheralFiles({ params: { id: id! } });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'os anexos'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

export function usePeripheralActivity(id: number | null) {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: [...ROOT, 'atividade', tenantId, id],
    queryFn: async (): Promise<ClaimActivity[]> => {
      const r = await api.claimPeripherals.listPeripheralActivity({
        params: { id: id! },
      });
      if (r.status !== 200) throw new Error(loadFailureMessage(r.status, 'a atividade'));
      return r.body;
    },
    enabled: id !== null && tenantId !== undefined,
  });
}

/** Comentar, editar e remover — a forma que a linha do tempo da mesa (`ActivityTimeline`) pede. */
export function usePeripheralCommentMutations(id: number) {
  const client = useQueryClient();
  const done = () => client.invalidateQueries({ queryKey: ROOT });
  return {
    comment: useMutation({
      mutationFn: async (body: string): Promise<ClaimComment> =>
        ok(
          await api.claimPeripherals.createPeripheralComment({
            params: { id },
            body: { body },
          }),
          201,
          'Não foi possível comentar.',
        ),
      onSuccess: done,
    }),
    editComment: useMutation({
      mutationFn: async ({ commentId, body }: { commentId: number; body: string }) =>
        ok(
          await api.claimPeripherals.updatePeripheralComment({
            params: { id, commentId },
            body: { body },
          }),
          200,
          'Não foi possível editar o comentário.',
        ),
      onSuccess: done,
    }),
    removeComment: useMutation({
      mutationFn: async (commentId: number) =>
        ok(
          await api.claimPeripherals.archivePeripheralComment({
            params: { id, commentId },
            body: {},
          }),
          200,
          'Não foi possível remover o comentário.',
        ),
      onSuccess: done,
    }),
  };
}

/** Grava as etiquetas do acionamento que são dos setores de quem grava. */
export function useSetPeripheralLabels(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (labelIds: number[]) =>
      ok(
        await api.claimPeripherals.setPeripheralLabels({
          params: { id },
          body: { labelIds },
        }),
        200,
        'Não foi possível gravar as etiquetas.',
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ROOT }),
  });
}

/** Define, troca ou tira (`null`) o vencimento do acionamento. */
export function useSetPeripheralDueDate(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (dueAt: string | null) =>
      ok(
        await api.claimPeripherals.setPeripheralDueDate({
          params: { id },
          body: { dueAt },
        }),
        200,
        'Não foi possível gravar o vencimento.',
      ),
    onSettled: () => client.invalidateQueries({ queryKey: ROOT }),
  });
}

export type { PeripheralSummary };
